/**
 * PayPal Pay Later Integration for Shopify (Prestige Theme + Live Product Options)
 */

class PayPalPayLater {
  constructor() {
    this.currentPrice = 0;
    this.initialized = false;
    this.container = null;
    this.priceCheckAttempts = 0;
    this.maxPriceCheckAttempts = 10;
    
    // Updated selectors for Prestige theme
    this.config = {
      // Price selectors - ORDER MATTERS (most specific first)
      priceSelectors: [
        'sale-price.h4.text-on-sale',           // Sale price (custom element)
        'sale-price',                            // Fallback sale price
        '.price-list--product sale-price',       // Product page sale price
        '.product-info__prices sale-price',      // Alternative location
        'compare-at-price',                      // Compare price if no sale
        '.price-list--product .h4',              // Generic h4 in price list
        '.product-info__prices .h4'              // Alternative h4
      ],
      messageContainerId: 'paypal-message-container',
      // Live Product Options events
      priceChangeEvents: [
        'variant:change',
        'price:updated',
        'liveoptions:change',
        'productOptionsChanged'
      ]
    };
  }

  /**
   * Automatically create the container if it doesn't exist
   */
  autoCreateContainer() {
    // Check if container already exists
    if (document.getElementById(this.config.messageContainerId)) {
      console.log('✓ Container already exists');
      return;
    }
    
    // Find the price element
    const priceElement = document.querySelector('.product-info__prices, .price-list--product');
    
    if (priceElement) {
      // Create the container
      const container = document.createElement('div');
      container.id = this.config.messageContainerId;
      container.style.margin = '15px 0';
      container.style.minHeight = '20px';
      
      // Insert after the price element
      priceElement.parentNode.insertBefore(container, priceElement.nextSibling);
      
      console.log('✓ PayPal container auto-created');
    } else {
      console.warn('⚠ Could not auto-create container - price element not found');
    }
  }

  /**
   * Get the final calculated price from the page
   */
  getFinalPrice() {
    // Try each price selector
    for (let selector of this.config.priceSelectors) {
      const priceElement = document.querySelector(selector);
      
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.innerText;
        // Match price patterns: $139.96
        const priceMatch = priceText.match(/\$?([\d,]+\.?\d*)/);
        
        if (priceMatch) {
          const price = parseFloat(priceMatch[1].replace(/,/g, ''));
          if (price > 0) {
            console.log(`✓ Found price: $${price} from selector: ${selector}`);
            return price;
          }
        }
      }
    }

    console.warn('Could not find price on page');
    return 0;
  }

  /**
   * Update PayPal message with new price
   */
  updatePayPalMessage(newPrice) {
    // Don't update if price hasn't changed or is invalid
    if (newPrice === this.currentPrice || newPrice <= 0) {
      return;
    }

    // Don't show PayPal for very low prices (below $30 minimum)
    if (newPrice < 30) {
      console.log('Price below PayPal minimum ($30), hiding message');
      if (this.container) {
        this.container.style.display = 'none';
      }
      return;
    }

    this.currentPrice = newPrice;

    if (!this.container) {
      this.container = document.getElementById(this.config.messageContainerId);
    }

    if (!this.container) {
      console.warn('PayPal container not found');
      return;
    }

    if (!window.paypal_sdk?.Messages) {
      console.warn('PayPal SDK not loaded yet');
      return;
    }

    console.log(`🔄 Updating PayPal message with price: $${newPrice.toFixed(2)}`);

    // Show container if hidden
    this.container.style.display = 'block';

    // Clear existing message
    this.container.innerHTML = '';

    // Render new message
    window.paypal_sdk.Messages({
      amount: newPrice,
      placement: 'product',
      style: {
        layout: 'text',
        logo: {
          type: 'inline'
        },
        text: {
          color: 'black',
          size: 12
        }
      }
    }).render(`#${this.config.messageContainerId}`);
  }

  /**
   * Initialize PayPal message with initial price
   */
  initialize() {
    if (this.initialized) return;

    // Wait for PayPal SDK
    if (!window.paypal_sdk) {
      if (this.priceCheckAttempts < this.maxPriceCheckAttempts) {
        this.priceCheckAttempts++;
        setTimeout(() => this.initialize(), 200);
      }
      return;
    }

    this.container = document.getElementById(this.config.messageContainerId);
    
    if (!this.container) {
      console.warn('PayPal message container not found - add <div id="paypal-message-container"></div> to your product template');
      return;
    }

    const initialPrice = this.getFinalPrice();
    
    if (initialPrice > 0) {
      this.updatePayPalMessage(initialPrice);
      this.initialized = true;
      console.log('✓ PayPal Pay Later initialized successfully');
    } else if (this.priceCheckAttempts < this.maxPriceCheckAttempts) {
      // Retry if price not found yet (waiting for Live Product Options to load)
      this.priceCheckAttempts++;
      setTimeout(() => this.initialize(), 300);
    }
  }

  /**
   * Watch for price changes in the DOM
   */
  observePriceChanges() {
    // Watch the main price container for any changes
    const priceContainer = document.querySelector('.price-list--product, .product-info__prices');

    if (!priceContainer) {
      console.warn('Price container not found for observation');
      return;
    }

    // Create mutation observer
    const observer = new MutationObserver(() => {
      const newPrice = this.getFinalPrice();
      if (newPrice > 0) {
        this.updatePayPalMessage(newPrice);
      }
    });

    // Start observing with more aggressive settings to catch Live Product Options changes
    observer.observe(priceContainer, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true
    });

    console.log('✓ Watching for price changes');
  }

  /**
   * Listen for custom price change events
   */
  setupEventListeners() {
    // Listen for all configured events
    this.config.priceChangeEvents.forEach(eventName => {
      document.addEventListener(eventName, (event) => {
        console.log(`Event detected: ${eventName}`);
        setTimeout(() => {
          const newPrice = event.detail?.price || this.getFinalPrice();
          if (newPrice > 0) {
            this.updatePayPalMessage(newPrice);
          }
        }, 100);
      });
    });

    // Listen for any clicks on product options (Live Product Options app)
    document.addEventListener('click', (e) => {
      if (e.target.closest('.cl-po--swatch-item, .product-option, [data-option]')) {
        console.log('Product option clicked');
        setTimeout(() => {
          const newPrice = this.getFinalPrice();
          if (newPrice > 0) {
            this.updatePayPalMessage(newPrice);
          }
        }, 500); // Longer delay for Live Product Options to calculate
      }
    });

    // Listen for form changes
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      productForm.addEventListener('change', () => {
        setTimeout(() => {
          const newPrice = this.getFinalPrice();
          if (newPrice > 0) {
            this.updatePayPalMessage(newPrice);
          }
        }, 300);
      });
    }

    console.log('✓ Event listeners configured');
  }

  /**
   * Start the integration
   */
  start() {
    console.log('🚀 Starting PayPal Pay Later integration...');

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => {
          this.initialize();
          this.observePriceChanges();
          this.setupEventListeners();
        }, 500); // Initial delay for Live Product Options
      });
    } else {
      setTimeout(() => {
        this.initialize();
        this.observePriceChanges();
        this.setupEventListeners();
      }, 500);
    }

    // Periodic check for first 15 seconds (Live Product Options can be slow)
    let checkCount = 0;
    const intervalId = setInterval(() => {
      const price = this.getFinalPrice();
      if (price > 0) {
        this.updatePayPalMessage(price);
      }
      
      checkCount++;
      if (checkCount >= 7) {
        clearInterval(intervalId);
        console.log('✓ Stopped periodic price checks');
      }
    }, 2000);
  }
}

// Initialize the integration
const paypalPayLater = new PayPalPayLater();
paypalPayLater.start();

// Expose for manual updates
window.updatePayPalPrice = function(price) {
  console.log(`Manual price update: $${price}`);
  paypalPayLater.updatePayPalMessage(parseFloat(price));
};

// Debug helper
window.debugPayPal = function() {
  console.log('Current Price:', paypalPayLater.currentPrice);
  console.log('PayPal SDK loaded:', !!window.paypal_sdk);
  console.log('Container exists:', !!document.getElementById('paypal-message-container'));
  console.log('Price found:', paypalPayLater.getFinalPrice());
};